import { conversionToTreeViewItem } from "@helpers/generals";
import { baseApi } from "./baseApi";
import {
    CashFlow,
    CustomerSupplierInvoice,
    Expenditure,
    MonthlyTotalPayroll,
    PalencaData,
    ProductService,
    Sale,
} from "@interfaces/syntage";
import {
    BalanceSheetInternal,
    BilInfo,
    CertificateTax,
    CreditBureauInfo,
    CreditDetailRecord,
    DashboardDataQuery,
    GigEarningsResponse,
    GigInfoResponse,
    InternalDashboard,
    InvoicesResponse,
    KPIsModel,
    MediaAlertPerson,
    ModelDownloadStatus,
    MopStats,
    PaymentCapacity,
    PaymentCapacitySubResponse,
    PersonToSearchMediaAlert,
    PersonToSearchMediaAlertResponse,
    RpcInfo,
    RugInfo,
    VeriphoneAllDataResponse,
} from "@interfaces/model";

import { UUID } from "crypto";
import { Response, ResponsePagination } from "@interfaces/response";
import { ClientSuppliers } from "@interfaces/references";

import IncomeStatement from "../dataDummy/incomeStatement.json";
import MonthlySales from "../dataDummy/monthlySales.json";
import MonthlyExpenditures from "../dataDummy/monthlyExpenditures.json";
import MonthlyCashFlow from "../dataDummy/monthlyCashFlow.json";
import MonthlyCustomerSupplierInvoice from "../dataDummy/monthlyCustomerSupplierInvoice.json";
import MonthlyProductsAnsServices from "../dataDummy/monthlyProductsAnsServices.json";
import BalanceSheetData from "../dataDummy/balanceSheet.json";
import RugInfoData from "../dataDummy/rugInfo.json";
import RPCInfoData from "../dataDummy/rpcInfo.json";
import PalencaInfoData from "../dataDummy/palenca.json";
import TaxKPIsData from "../dataDummy/taxKpis.json";
import InvoiceDetails from "../dataDummy/invoiceDetails.json";
import CreditBureauGeneralData from "../dataDummy/creditBureauGeneralData.json";
import CreditBureauCountCurrentCredits from "../dataDummy/creditBureauCountCurrentCredits.json";
import CreditBureauMopsPercentage from "../dataDummy/creditBureauMopsPercentage.json";
import CreditBureauMopsMetrics from "../dataDummy/creditBureauMopsMetrics.json";
import CreditBureauMatrix from "../dataDummy/creditBureauMatrix.json";
import ModelDownloadStatusData from "../dataDummy/modelDownloadStatus.json";
import AbortiveReviewsStatus from "../dataDummy/abortivesReviewsStatus.json";
import InternalBalanceSheetData from "../dataDummy/internalBalanceSheet.json";
import InternalDashboardData from "../dataDummy/internalDashboards.json";
import CsfData from "../dataDummy/csf.json";

const satCore = baseApi.injectEndpoints({
    endpoints: (build) => ({
        postMonthlySales: build.mutation<Array<Sale>, DashboardDataQuery>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.rfc === "BPP2207121N2") {
                    const startDate = new Date(args.date_from);
                    const endDate = new Date(args.date_to);

                    return {
                        data: MonthlySales.filter((item) => {
                            const itemDate = new Date(item.startDate);
                            return itemDate >= startDate && itemDate <= endDate;
                        }),
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/dashboards/monthly-total-sales-revenue`,
                    method: "POST",
                    body: args,
                });

                return result.data;
            },
        }),
        postMonthlyExpenditures: build.mutation<Array<Expenditure>, DashboardDataQuery>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.rfc === "BPP2207121N2") {
                    const startDate = new Date(args.date_from);
                    const endDate = new Date(args.date_to);
                    return {
                        data: MonthlyExpenditures.filter((item) => {
                            const itemDate = new Date(item.startDate);
                            return itemDate >= startDate && itemDate <= endDate;
                        }),
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/dashboards/monthly-total-expenditures`,
                    method: "POST",
                    body: args,
                });

                return result.data;
            },
        }),
        postMonthlyCashFlow: build.mutation<Array<CashFlow>, DashboardDataQuery>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.rfc === "BPP2207121N2") {
                    const startDate = new Date(args.date_from);
                    const endDate = new Date(args.date_to);

                    return {
                        data: MonthlyCashFlow.filter((item) => {
                            const itemDate = new Date(item.startDate);
                            return itemDate >= startDate && itemDate <= endDate;
                        }),
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/dashboards/monthly-total-cash-flow`,
                    method: "POST",
                    body: args,
                });

                return result.data;
            },
        }),
        postMonthlyCustomerSupplierInvoice: build.mutation<CustomerSupplierInvoice, DashboardDataQuery>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.rfc === "BPP2207121N2") {
                    const startDate = new Date(args.date_from);
                    const endDate = new Date(args.date_to);
                    return {
                        data: {
                            clients: MonthlyCustomerSupplierInvoice.clients.map((client) => ({
                                ...client,
                                transactions: client.transactions.filter((transaction) => {
                                    const itemDate = new Date(transaction.date);
                                    return itemDate >= startDate && itemDate <= endDate;
                                }),
                            })),
                            suppliers: MonthlyCustomerSupplierInvoice.suppliers.map((supplier) => ({
                                ...supplier,
                                transactions: supplier.transactions.filter((transaction) => {
                                    const itemDate = new Date(transaction.date);
                                    return itemDate >= startDate && itemDate <= endDate;
                                }),
                            })),
                        },
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/dashboards/customer-supplier-invoice-concentration`,
                    method: "POST",
                    body: args,
                });

                return result.data;
            },
        }),
        postMonthlyProductsAnsServices: build.mutation<
            {
                bought: Array<ProductService>;
                sold: Array<ProductService>;
            },
            DashboardDataQuery
        >({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.rfc === "BPP2207121N2") {
                    const startDate = new Date(args.date_from);
                    const endDate = new Date(args.date_to);
                    return {
                        data: {
                            bought: MonthlyProductsAnsServices.bought.map((item) => ({
                                ...item,
                                transactions: item.transactions.filter((transaction) => {
                                    const itemDate = new Date(transaction.date);
                                    return itemDate >= startDate && itemDate <= endDate;
                                }),
                            })),
                            sold: MonthlyProductsAnsServices.sold.map((item) => ({
                                ...item,
                                transactions: item.transactions.filter((transaction) => {
                                    const itemDate = new Date(transaction.date);
                                    return itemDate >= startDate && itemDate <= endDate;
                                }),
                            })),
                        },
                    };
                }
                const result = await baseQuery({
                    url: `/sat_core/dashboards/products-and-services`,
                    method: "POST",
                    body: args,
                });

                return result.data;
            },
        }),
        postMonthlyTotalPayroll: build.mutation<Array<MonthlyTotalPayroll>, DashboardDataQuery>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                const result = await baseQuery({
                    url: `/sat_core/dashboards/monthly-total-payroll`,
                    method: "POST",
                    body: args,
                });
                return result.data;
            },
        }),
        postBalanceSheet: build.mutation<Array<any>, DashboardDataQuery>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.rfc === "BPP2207121N2") {
                    const tmp = {
                        data: BalanceSheetData.map((item) => conversionToTreeViewItem(item, 0)),
                    };

                    return tmp;
                }

                const result = await baseQuery({
                    url: `/sat_core/dashboards/balance-sheet`,
                    method: "POST",
                    body: args,
                });

                if (Array.isArray(result.data) || result.data.data.data?.length === 0) {
                    return [];
                }

                return {
                    data: result.data.data.data.map((item) => conversionToTreeViewItem(item, 0)),
                };
            },
        }),
        postIncomeStatement: build.mutation<Array<any>, DashboardDataQuery>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.rfc === "BPP2207121N2") {
                    return {
                        data: IncomeStatement.map((item) => conversionToTreeViewItem(item, 0)),
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/dashboards/income-statement`,
                    method: "POST",
                    body: args,
                });

                if (typeof result.data?.data != "object") {
                    return [];
                }

                return {
                    data: result.data.data.data.map((item) => conversionToTreeViewItem(item, 0)),
                };
            },
        }),
        postModelDownloadStatus: build.mutation<ModelDownloadStatus, { applicationId: UUID; rfc: string }>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.rfc === "BPP2207121N2") {
                    return {
                        data: ModelDownloadStatusData,
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/dashboards/model-status`,
                    method: "POST",
                    body: {
                        rfc: args.rfc,
                        loan_application_id: args.applicationId,
                    },
                });

                return result.data;
            },
        }),
        postRugInfo: build.mutation<Array<RugInfo>, { loanApplicationId: UUID; rfc: string }>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.rfc === "BPP2207121N2") {
                    return {
                        data: RugInfoData,
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/dashboards/rug-info`,
                    method: "POST",
                    body: args,
                });

                return result.data;
            },
        }),
        postRpcInfo: build.mutation<Response<RpcInfo>, string>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args === "BPP2207121N2") {
                    return {
                        data: RPCInfoData,
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/dashboards/rpc-info`,
                    method: "POST",
                    body: {
                        rfc: args,
                    },
                });

                return result;
            },
        }),
        getActFile: build.query<Response<string>, { loanApplicationId: UUID; syntageFileId: UUID }>({
            query: ({ loanApplicationId, syntageFileId }) => ({
                url: `/sat_core/dashboards/syntage-file/${loanApplicationId}/file/${syntageFileId}`,
            }),
        }),
        postRetryRunModel: build.mutation<void, any>({
            query: (body) => ({
                url: `/sat_core/run_model`,
                method: "POST",
                body: body,
            }),
        }),
        postRfcInvoices: build.mutation<void, any>({
            query: (body) => ({
                url: "/sat_core/update_rfc_invoices",
                method: "POST",
                body: body,
            }),
        }),
        postCreditBureauReconsultAuth: build.mutation<void, any>({
            query: (body) => ({
                url: "/sat_core/credit_bureau_reconsult_authorization",
                method: "POST",
                body: body,
            }),
        }),
        postPalenca: build.mutation<PalencaData, UUID>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args === "3912e960-7795-434f-bc47-d277ff43937e") {
                    return {
                        data: PalencaInfoData,
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/buro_ingresos`,
                    method: "POST",
                    body: {
                        loan_application_id: args,
                    },
                });

                return result.data;
            },
        }),
        getClientReferences: build.query<Array<ClientSuppliers>, string>({
            query: (params) => ({
                url: `/sat_core/clients_and_suppliers?${params}`,
            }),
            transformResponse: (response: ResponsePagination<ClientSuppliers>) => {
                return response.data.data;
            },
        }),
        postPaymentCapacity: build.mutation<
            PaymentCapacity,
            {
                applicationId: UUID;
                clientRfc: string;
                is_pf?: boolean;
                client_income?: Array<number>;
                client_avg_monthly_amount?: Array<number>;
                endorsement_avg_monthly_amount?: Array<number>;
                rate?: number;
                term?: number;
            }
        >({
            query: (body) => ({
                url: "/sat_core/payment_capacity",
                method: "POST",
                body: {
                    loan_application_id: body.applicationId,
                    client_rfc: body.clientRfc,
                    is_pf: body.is_pf,
                    client_income: body.client_income,
                    client_avg_monthly_amount: body.client_avg_monthly_amount,
                    endorsement_avg_monthly_amount: body.endorsement_avg_monthly_amount,
                    rate: body.rate,
                    term: body.term,
                },
            }),
            transformResponse: (response: Response<PaymentCapacitySubResponse>) => {
                return response.data.data;
            },
        }),
        getTaxKpis: build.query<Array<KPIsModel>, string>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.includes("BPP2207121N2")) {
                    return {
                        data: TaxKPIsData,
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/dashboards/tax-return-kpis?${args}`,
                });

                return result.data;
            },
        }),
        getInvoiceDetails: build.query<InvoicesResponse, string>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.includes("BPP2207121N2")) {
                    return {
                        data: InvoiceDetails,
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/dashboards/invoices?${args}`,
                });

                return result.data;
            },
        }),
        getInvoiceFile: build.query<Response<any>, { loanApplicationId: UUID; invoiceFileId: UUID }>({
            query: ({ loanApplicationId, invoiceFileId }) => ({
                url: `/sat_core/dashboards/invoice-file/${loanApplicationId}/file/${invoiceFileId}`,
            }),
        }),
        getCreditBureauGeneralData: build.query<CreditBureauInfo, string>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.includes("BPP2207121N2")) {
                    return CreditBureauGeneralData;
                }

                const result = await baseQuery({
                    url: `/sat_core/bureau/general_data?${args}`,
                });

                return result.data;
            },
        }),
        getCreditBureauCountCurrentCredits: build.query<Record<string, number>, string>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.includes("BPP2207121N2")) {
                    return {
                        data: CreditBureauCountCurrentCredits,
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/bureau/count_current_credits?${args}`,
                });

                return result.data;
            },
        }),
        getCreditBureauMopsPercentage: build.query<Record<string, MopStats>, string>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.includes("BPP2207121N2")) {
                    return {
                        data: CreditBureauMopsPercentage,
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/bureau/mops_percentage?${args}`,
                });

                return result.data;
            },
        }),
        getCreditBureauMopsMetrics: build.query<Record<string, Record<string, MopStats>>, string>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.includes("BPP2207121N2")) {
                    return {
                        data: CreditBureauMopsMetrics,
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/bureau/mops_metrics?${args}`,
                });

                return result.data;
            },
        }),
        getCreditBureauMatrix: build.query<Array<CreditDetailRecord>, string>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.includes("BPP2207121N2")) {
                    return {
                        data: CreditBureauMatrix.map((record) => ({
                            contract: record.Contrato,
                            lenderType: record["Tipo Otorgante"],
                            currency: record.Moneda,
                            status: record.Status,
                            type: record["Tipo Credito"],
                            code: record["Codigo credito"],
                            termVsRevolving: record["Simple vs Revolvente"],
                            detail: record.Detalle,
                            openingDate: record.Apertura,
                            closingDate: record.Cierre,
                            term: record.Plazo,
                            originalAmount: record.Original,
                            currentAmount: record.Vigente,
                            lastUpdatedPeriod: record["Ultimo Periodo Actualizado"],
                            history: record.Historicos,
                            maturityDistribution_129: record["1-29 días"],
                            maturityDistribution_3059: record["30-59 días"],
                            maturityDistribution_6089: record["60-89 días"],
                            maturityDistribution_90119: record["90-119 días"],
                            maturityDistribution_120179: record["120-179 días"],
                            maturityDistribution_180: record["180+ días"],
                        })),
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/bureau/matrix?${args}`,
                });

                return {
                    data: result.data.data.map((record) => ({
                        contract: record.Contrato,
                        lenderType: record["Tipo Otorgante"],
                        currency: record.Moneda,
                        status: record.Status,
                        type: record["Tipo Credito"],
                        code: record["Codigo credito"],
                        termVsRevolving: record["Simple vs Revolvente"],
                        detail: record.Detalle,
                        openingDate: record.Apertura,
                        closingDate: record.Cierre,
                        term: record.Plazo,
                        originalAmount: record.Original,
                        currentAmount: record.Vigente,
                        lastUpdatedPeriod: record["Ultimo Periodo Actualizado"],
                        history: record.Historicos,
                        maturityDistribution_129: record["1-29 días"],
                        maturityDistribution_3059: record["30-59 días"],
                        maturityDistribution_6089: record["60-89 días"],
                        maturityDistribution_90119: record["90-119 días"],
                        maturityDistribution_120179: record["120-179 días"],
                        maturityDistribution_180: record["180+ días"],
                    })),
                };
            },
        }),
        postGigInfo: build.mutation<GigInfoResponse[], { rfc: string; loanApplicationId: UUID }>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                const result = await baseQuery({
                    url: `/sat_core/dashboards/gig-info`,
                    method: "POST",
                    body: {
                        rfc: args.rfc,
                        loan_application_id: args.loanApplicationId,
                    },
                });

                if (result.error) return { error: result.error };

                const data = result.data as any;
                const items = data?.data?.data;
                if (!Array.isArray(items) || items.length === 0) {
                    return { error: { status: "CUSTOM_ERROR", error: data?.message ?? "No GIG info found" } };
                }

                return { data: items as GigInfoResponse[] };
            },
        }),
        postGigEarnings: build.mutation<
            GigEarningsResponse,
            { rfc: string; loanApplicationId: UUID; dateFrom: string; dateTo: string }
        >({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                const result = await baseQuery({
                    url: `/sat_core/dashboards/gig-earnings`,
                    method: "POST",
                    body: {
                        rfc: args.rfc,
                        loan_application_id: args.loanApplicationId,
                        date_from: args.dateFrom,
                        date_to: args.dateTo,
                    },
                });

                if (result.error) return { error: result.error };

                const data = result.data as any;

                // Normalize API envelope: some endpoints return { data: { data: [...] } }
                let payload = data;
                if (data?.data?.data) {
                    payload = Array.isArray(data.data.data) ? data.data.data[0] : data.data.data;
                } else if (data?.data && typeof data.data === "object") {
                    payload = data.data;
                }

                if (!payload?.profile) {
                    return { error: { status: "CUSTOM_ERROR", error: data?.message ?? "No GIG earnings found" } };
                }

                return { data: payload as GigEarningsResponse };
            },
        }),
        postVeriphoneAllData: build.mutation<
            VeriphoneAllDataResponse,
            {
                loanApplicationId: UUID;
                personUuid: UUID;
                rfc: string;
                phone?: string;
                countryCode?: string;
                email?: string;
                ipAddress?: string;
                userAgent?: string;
            }
        >({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                const body: Record<string, unknown> = {
                    loanApplicationId: args.loanApplicationId,
                    personUuid: args.personUuid,
                    rfc: args.rfc,
                    userLanguage: "es",
                    connectionIsMobileApp: false,
                };
                if (args.phone && args.countryCode) {
                    body.phoneNumber = { splitPhoneNumber: { countryCode: args.countryCode, phoneNumber: args.phone } };
                }
                if (args.email) body.email = args.email;
                if (args.ipAddress) body.ipAddress = args.ipAddress;
                if (args.userAgent) body.userAgent = args.userAgent;

                const result = await baseQuery({ url: `/sat_core/veriphone/all-data`, method: "POST", body });

                if (result.error) return { error: result.error };

                const data = result.data as any;
                const items = data?.data?.data;
                if (!Array.isArray(items)) {
                    return { error: { status: "CUSTOM_ERROR", error: data?.message ?? "No veriphone data found" } };
                }

                const maskIp = (ip: string) => (ip ? `${ip.slice(0, 4)}.°°°°.°°°°.°°°°` : ip);

                for (const item of items as any[]) {
                    if (item.entity === "veriphone_phone_insights") {
                        for (const assoc of item.result?.linkedIpAddresses?.associatedIpAddresses ?? []) {
                            if (assoc.ipAddress) assoc.ipAddress = maskIp(assoc.ipAddress);
                        }
                    }
                    if (item.entity === "veriphone_ip_insights") {
                        const checks = item.result?.securityChecks;
                        if (checks?.externalIpAddress) checks.externalIpAddress = maskIp(checks.externalIpAddress);
                    }
                }

                return { data: items as VeriphoneAllDataResponse };
            },
        }),
        getAbortivesReviewsStatus: build.query<
            any,
            { rfc: string; loanApplicationId: UUID; companyId: UUID; financialCode?: string }
        >({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.rfc === "BPP2207121N2") {
                    return {
                        data: AbortiveReviewsStatus,
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/abortives-reviews/status`,
                    params: args,
                });

                return result.data;
            },
        }),
        getExtraction: build.query<any, { loanApplicationId: UUID; extractorType: string }>({
            query: ({ loanApplicationId, extractorType }) => ({
                url: `/sat_core/extraction/${loanApplicationId}/${extractorType}`,
            }),
            transformResponse: (response: Response<any>) => {
                return response.data;
            },
        }),
        postBilInfo: build.mutation<BilInfo, { loanApplicationId: UUID; rfc: string }>({
            query: ({ loanApplicationId, rfc }) => ({
                url: `/sat_core/dashboards/bil-info`,
                method: "POST",
                body: {
                    rfc,
                    loan_application_id: loanApplicationId,
                },
            }),
            transformResponse: (response: Response<BilInfo>) => {
                return response.data;
            },
        }),
        postInternalBalanceSheet: build.mutation<Array<BalanceSheetInternal>, { loanApplicationId: UUID; rfc: string }>(
            {
                async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                    if (args.rfc === "SPR200627FY4") {
                        return {
                            data: InternalBalanceSheetData,
                        };
                    }

                    const result = await baseQuery({
                        url: `/sat_core/dashboards/internal-balance-sheet`,
                        method: "POST",
                        body: {
                            rfc: args.rfc,
                            loan_application_id: args.loanApplicationId,
                        },
                    });

                    return Array.isArray(result.data.data) ? result.data : { data: [] };
                },
            },
        ),
        getMediaAlertByPerson: build.query<MediaAlertPerson, UUID>({
            async queryFn(personId, _queryApi, _extraOptions, baseQuery) {
                const result = await baseQuery({
                    url: `/sat_core/dashboards/media-alert/${personId}`,
                });

                if (result.error) return { error: result.error };

                const response = result.data as Response<MediaAlertPerson>;

                if (!response.data || typeof response.data !== "object") {
                    return { error: { status: 404, data: response } };
                }

                return { data: response.data };
            },
        }),
        postSearchMediaAlertByPerson: build.mutation<PersonToSearchMediaAlertResponse, PersonToSearchMediaAlert>({
            query: (person) => ({
                url: `/sat_core/search-media-alert-person`,
                method: "POST",
                body: person,
            }),
            transformResponse: (response: Response<PersonToSearchMediaAlertResponse>) => {
                return response.data;
            },
        }),
        postInternalDashboard: build.mutation<Array<InternalDashboard>, { loanApplicationId: UUID; rfc: string }>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.rfc === "SPR200627FY4") {
                    return {
                        data: InternalDashboardData,
                    };
                }

                const result = await baseQuery({
                    url: `/sat_core/dashboards/internal-tax-return`,
                    method: "POST",
                    body: {
                        rfc: args.rfc,
                        loan_application_id: args.loanApplicationId,
                    },
                });

                return result.data;
            },
        }),
        getTaxCertificateStatus: build.query<Array<CertificateTax>, string>({
            async queryFn(args, _queryApi, _extraOptions, baseQuery) {
                if (args.includes("BPP2207121N2")) {
                    return {
                        data: CsfData.data,
                    };
                }
                const result = await baseQuery({
                    url: `/sat_core/certificate_tax_status?${args}`,
                });

                return result.data.data;
            },
        }),
        postTaxCertificateStatus: build.mutation<CertificateTax, CertificateTax>({
            query: (payload) => ({
                url: `/sat_core/certificate_tax_status`,
                method: "POST",
                body: payload,
            }),
            transformResponse: (response: Response<CertificateTax>) => {
                return response.data;
            },
        }),
        patchTaxCertificateStatus: build.mutation<CertificateTax, Partial<CertificateTax>>({
            query: (payload) => ({
                url: `/sat_core/certificate_tax_status/${payload.rfc}`,
                method: "PATCH",
                body: payload,
            }),
            transformResponse: (response: Response<CertificateTax>) => {
                return response.data;
            },
        }),
        postRpcConsultation: build.mutation<null, { loanApplicationId: UUID; rfc: string }>({
            query: ({ loanApplicationId, rfc }) => ({
                url: `/sat_core/rpc-consultation`,
                method: "POST",
                body: {
                    loan_application_id: loanApplicationId,
                    rfc,
                },
            }),
            transformResponse: (response: Response<null>) => {
                return response.data;
            },
        }),
        getRpcConsultation: build.query<RpcInfo, string>({
            query: (rfc) => `/sat_core/rpc-consultation?rfc=${rfc}`,
            transformResponse: (response: Response<RpcInfo>) => {
                return response.data;
            },
        }),
    }),
});

export const {
    usePostGigInfoMutation,
    usePostGigEarningsMutation,
    usePostMonthlySalesMutation,
    usePostMonthlyExpendituresMutation,
    usePostMonthlyCashFlowMutation,
    usePostMonthlyCustomerSupplierInvoiceMutation,
    usePostMonthlyProductsAnsServicesMutation,
    usePostMonthlyTotalPayrollMutation,
    usePostBalanceSheetMutation,
    usePostIncomeStatementMutation,
    usePostModelDownloadStatusMutation,
    usePostRugInfoMutation,
    usePostRpcInfoMutation,
    useLazyGetActFileQuery,
    usePostRetryRunModelMutation,
    usePostRfcInvoicesMutation,
    usePostCreditBureauReconsultAuthMutation,
    usePostPalencaMutation,
    useLazyGetClientReferencesQuery,
    useGetClientReferencesQuery,
    usePostPaymentCapacityMutation,
    useLazyGetTaxKpisQuery,
    useLazyGetInvoiceDetailsQuery,
    useLazyGetInvoiceFileQuery,
    useLazyGetCreditBureauGeneralDataQuery,
    useLazyGetCreditBureauCountCurrentCreditsQuery,
    useLazyGetCreditBureauMopsMetricsQuery,
    useLazyGetCreditBureauMopsPercentageQuery,
    useLazyGetCreditBureauMatrixQuery,
    useLazyGetAbortivesReviewsStatusQuery,
    usePostVeriphoneAllDataMutation,
    useLazyGetExtractionQuery,
    usePostBilInfoMutation,
    usePostInternalBalanceSheetMutation,
    useGetMediaAlertByPersonQuery,
    usePostSearchMediaAlertByPersonMutation,
    usePostInternalDashboardMutation,
    useLazyGetTaxCertificateStatusQuery,
    usePostTaxCertificateStatusMutation,
    usePatchTaxCertificateStatusMutation,
    usePostRpcConsultationMutation,
    useLazyGetRpcConsultationQuery,
} = satCore;
